import type { ReactNode } from 'react';
import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import type { Role } from '@em/shared';
import { useAuth, useSession } from '../../auth/AuthContext';
import { RequireRole } from '../../components/Layout';
import { PageHeader } from '../../components/ui';
import { AuditLog } from './AuditLog';
import { BusinessSettings } from './BusinessSettings';
import { CategoriesSettings } from './CategoriesSettings';
import { HolidaysSettings, PrivacySettings } from './HolidaysPrivacy';
import { JobsSettings } from './JobsSettings';
import { KiosksSettings } from './KiosksSettings';
import { MyobSettings } from './MyobSettings';
import { SitesSettings, TeamsSettings } from './SitesTeams';
import { UsersSettings } from './UsersSettings';

interface Section {
  path: string;
  label: string;
  min: Role;
  element: ReactNode;
  show?: boolean;
}

export function SettingsRoutes() {
  const { can } = useAuth();
  const session = useSession();
  const sections: Section[] = [
    { path: 'business', label: 'Business', min: 'payroll_admin', element: <BusinessSettings /> },
    { path: 'sites', label: 'Sites and geofences', min: 'payroll_admin', element: <SitesSettings /> },
    { path: 'teams', label: 'Teams', min: 'payroll_admin', element: <TeamsSettings /> },
    { path: 'users', label: 'Users and roles', min: 'owner', element: <UsersSettings /> },
    { path: 'kiosks', label: 'Kiosks', min: 'payroll_admin', element: <KiosksSettings /> },
    { path: 'categories', label: 'Payroll categories', min: 'payroll_admin', element: <CategoriesSettings /> },
    { path: 'myob', label: 'MYOB connection', min: 'payroll_admin', element: <MyobSettings /> },
    { path: 'holidays', label: 'Public holidays', min: 'payroll_admin', element: <HolidaysSettings /> },
    { path: 'privacy', label: 'Privacy notices', min: 'payroll_admin', element: <PrivacySettings /> },
    { path: 'jobs', label: 'Jobs', min: 'payroll_admin', element: <JobsSettings />, show: session.features.jobCosting },
    { path: 'audit', label: 'Audit log', min: 'payroll_admin', element: <AuditLog /> },
  ];
  const visible = sections.filter((s) => s.show !== false && can(s.min));

  return (
    <>
      <PageHeader title="Settings" />
      <nav className="tabs" aria-label="Settings sections">
        {visible.map((s) => (
          <NavLink key={s.path} to={`/settings/${s.path}`}>
            {s.label}
          </NavLink>
        ))}
      </nav>
      <Routes>
        <Route index element={<Navigate to={visible[0] ? visible[0].path : 'business'} replace />} />
        {sections
          .filter((s) => s.show !== false)
          .map((s) => (
            <Route key={s.path} path={s.path} element={<RequireRole min={s.min}>{s.element}</RequireRole>} />
          ))}
        <Route path="*" element={<Navigate to="/settings" replace />} />
      </Routes>
    </>
  );
}
